import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';

import CockpitNavigation from './CockpitNavigation';
import { chargerCockpitOperationnel } from './cockpitOperationnelApi';

const useStyles = makeStyles((theme) => ({
  page: { paddingTop: theme.spacing(3), paddingBottom: theme.spacing(4), textAlign: 'left' },
  paper: { padding: theme.spacing(2), marginBottom: theme.spacing(2), boxShadow: 'none', border: '1px solid #e5e7eb' },
  header: { display: 'flex', justifyContent: 'space-between', gap: theme.spacing(2), flexWrap: 'wrap', alignItems: 'center' },
  muted: { color: '#6b7280', fontSize: 12 },
  grille: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: theme.spacing(1), marginTop: theme.spacing(1.5) },
  bloc: { border: '1px solid #e5e7eb', borderRadius: 6, padding: theme.spacing(1), backgroundColor: '#fff' },
  valeur: { fontWeight: 700, fontSize: 22, lineHeight: 1.2 },
  label: { color: '#6b7280', fontSize: 11, textTransform: 'uppercase' },
  tableScroll: { overflowX: 'auto' },
  chipVert: { backgroundColor: '#ecfdf5', color: '#065f46', fontSize: 11 },
  chipOrange: { backgroundColor: '#fff7ed', color: '#9a3412', fontSize: 11 },
  chipRouge: { backgroundColor: '#fef2f2', color: '#991b1b', fontSize: 11 },
  chipBleu: { backgroundColor: '#eff6ff', color: '#1d4ed8', fontSize: 11 },
  erreur: { borderLeft: '4px solid #f59e0b', padding: theme.spacing(1.5), backgroundColor: '#fff7ed', marginTop: theme.spacing(1.5) },
}));

const CockpitAmeliorationSysteme = () => {
  const classes = useStyles();
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');

  const charger = () => {
    setChargement(true);
    chargerCockpitOperationnel()
      .then((payload) => {
        setDonnees(payload);
        setErreur('');
        setChargement(false);
      })
      .catch((err) => {
        setErreur(err.message);
        setChargement(false);
      });
  };

  useEffect(() => {
    charger();
  }, []);

  const amelioration = useMemo(() => calculerAmelioration(donnees), [donnees]);

  return (
    <Container maxWidth={false} className={classes.page}>
      <CockpitNavigation vueActive="amelioration" />
      <Paper className={classes.paper}>
        <div className={classes.header}>
          <div>
            <Typography variant="h4">Amelioration systeme</Typography>
            <Typography variant="body2" className={classes.muted}>
              Fragilites recurrentes, dette runtime et zones a corriger sans bruit enterprise.
            </Typography>
          </div>
          <Button variant="outlined" size="small" onClick={charger} disabled={chargement}>
            {chargement ? 'Chargement...' : 'Actualiser'}
          </Button>
        </div>
        {erreur && (
          <div className={classes.erreur}>
            <Typography variant="subtitle2">Erreur backend</Typography>
            <Typography variant="body2">{erreur}</Typography>
          </div>
        )}
        {donnees && <ScoreAmelioration amelioration={amelioration} classes={classes} />}
      </Paper>

      {donnees && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <SectionPriorites items={amelioration.priorites} classes={classes} />
          </Grid>
          <Grid item xs={12} md={7}>
            <SectionHotspots versions={amelioration.hotspotsApk} classes={classes} />
          </Grid>
          <Grid item xs={12}>
            <SectionFamilles familles={amelioration.famillesIncidents} classes={classes} />
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

const ScoreAmelioration = ({ amelioration, classes }) => (
  <div className={classes.grille}>
    <Kpi classes={classes} label="Score amelioration" valeur={`${amelioration.score}/100`} alerte={amelioration.score < 70} />
    <Kpi classes={classes} label="Fragilites ouvertes" valeur={amelioration.priorites.length} alerte={amelioration.priorites.length > 0} />
    <Kpi classes={classes} label="Hotspots APK" valeur={amelioration.hotspotsApk.length} alerte={amelioration.hotspotsApk.length > 0} />
    <Kpi classes={classes} label="Familles incidents" valeur={amelioration.famillesIncidents.length} alerte={amelioration.famillesIncidents.length > 0} />
  </div>
);

const SectionPriorites = ({ items, classes }) => (
  <Paper className={classes.paper}>
    <Typography variant="h6">Priorites candidates</Typography>
    <Typography variant="body2" className={classes.muted}>
      Liste volontairement courte: corriger ce qui revient, pas tout mesurer.
    </Typography>
    <Table size="small">
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.cle}>
            <TableCell>
              <strong>{item.titre}</strong>
              <Typography variant="body2" className={classes.muted}>{item.explication}</Typography>
            </TableCell>
            <TableCell>
              <Chip size="small" label={item.niveau} className={item.niveau === 'critique' ? classes.chipRouge : classes.chipOrange} />
            </TableCell>
          </TableRow>
        ))}
        {items.length === 0 && (
          <TableRow><TableCell>Aucune priorite systeme evidente dans le payload actuel.</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  </Paper>
);

const SectionHotspots = ({ versions, classes }) => (
  <Paper className={classes.paper}>
    <Typography variant="h6">Hotspots APK</Typography>
    <Typography variant="body2" className={classes.muted}>
      Versions qui portent des signaux de regression ou de fragilite runtime.
    </Typography>
    <div className={classes.tableScroll}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Version</TableCell>
            <TableCell>Score risque</TableCell>
            <TableCell>Incidents</TableCell>
            <TableCell>Finalize suspects</TableCell>
            <TableCell>RAW absentes</TableCell>
            <TableCell>Uploads bloques</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {versions.slice(0, 12).map((version) => (
            <TableRow key={version.version_apk || version.version || 'n/d'}>
              <TableCell><strong>{version.version_apk || version.version || 'n/d'}</strong></TableCell>
              <TableCell><Chip size="small" label={version.scoreRisque} className={version.scoreRisque >= 8 ? classes.chipRouge : classes.chipOrange} /></TableCell>
              <TableCell>{nombre(version.nb_incidents)}</TableCell>
              <TableCell>{nombre(version.nb_finalize_suspects)}</TableCell>
              <TableCell>{nombre(version.nb_raw_absentes)}</TableCell>
              <TableCell>{nombre(version.nb_uploads_bloques)}</TableCell>
            </TableRow>
          ))}
          {versions.length === 0 && (
            <TableRow><TableCell colSpan={6}>Aucun hotspot APK expose.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  </Paper>
);

const SectionFamilles = ({ familles, classes }) => (
  <Paper className={classes.paper}>
    <Typography variant="h6">Familles incident recurrentes</Typography>
    <Typography variant="body2" className={classes.muted}>
      Regroupement simple pour nourrir les prochaines corrections runtime.
    </Typography>
    <div className={classes.grille}>
      {familles.map((famille) => (
        <div className={classes.bloc} key={famille.nom}>
          <div className={classes.label}>{famille.nom}</div>
          <div className={classes.valeur}>{famille.nombre}</div>
          <Chip size="small" label={famille.niveau} className={famille.niveau === 'critique' ? classes.chipRouge : classes.chipBleu} />
        </div>
      ))}
      {familles.length === 0 && (
        <Typography variant="body2" className={classes.muted}>Aucune famille recurrente detectee.</Typography>
      )}
    </div>
  </Paper>
);

const Kpi = ({ classes, label, valeur, alerte }) => (
  <div className={classes.bloc} style={alerte ? { borderColor: '#f59e0b', backgroundColor: '#fff7ed' } : null}>
    <div className={classes.label}>{label}</div>
    <div className={classes.valeur}>{valeur}</div>
  </div>
);

function calculerAmelioration(donnees) {
  const source = donnees || {};
  const dette = source.detteRuntime || {};
  const versions = source.versionsApk || [];
  const incidents = source.incidentsCritiques || source.incidents || [];
  const priorites = construirePriorites(dette, source, versions);
  const hotspotsApk = versions
    .map((version) => ({ ...version, scoreRisque: scoreVersion(version) }))
    .filter((version) => version.scoreRisque > 0)
    .sort((a, b) => b.scoreRisque - a.scoreRisque);
  const famillesIncidents = construireFamilles(incidents);
  const score = Math.max(0, Math.min(100, 100 - priorites.length * 12 - hotspotsApk.length * 4 - famillesIncidents.length * 5));

  return {
    score,
    priorites,
    hotspotsApk,
    famillesIncidents,
  };
}

function construirePriorites(dette, source, versions) {
  const priorites = [];
  const fauxSucces = (source.fauxSuccesCritiques || source.fauxSucces || []).length;
  const uploads = Number(dette.uploads_sans_progression || 0) + somme(versions, 'nb_uploads_bloques');
  const etats202 = Number(dette.etats_202_stagnants || 0) + somme(versions, 'nb_202_actifs');
  const finalize = somme(versions, 'nb_finalize_suspects');
  const raw = somme(versions, 'nb_raw_absentes');

  if (fauxSucces > 0 || finalize > 0 || raw > 0) {
    priorites.push({
      cle: 'faux-succes',
      titre: 'Reduire les faux succes',
      niveau: 'critique',
      explication: `${fauxSucces + finalize + raw} signal(aux) ou la preuve utile manque apres un succes apparent.`,
    });
  }
  if (uploads > 0) {
    priorites.push({
      cle: 'uploads',
      titre: 'Debloquer les uploads sans progression',
      niveau: uploads >= 3 ? 'critique' : 'a surveiller',
      explication: `${uploads} upload(s) semblent rester actifs sans progression utile.`,
    });
  }
  if (etats202 > 0) {
    priorites.push({
      cle: 'etat-202',
      titre: 'Clarifier les etats 202 stagnants',
      niveau: etats202 >= 3 ? 'critique' : 'a surveiller',
      explication: `${etats202} etat(s) 202 risquent de masquer une convergence lente.`,
    });
  }

  return priorites;
}

function construireFamilles(incidents) {
  const compteurs = {};
  (incidents || []).forEach((incident) => {
    const famille = categoriserIncident(incident);
    compteurs[famille] = (compteurs[famille] || 0) + 1;
  });

  return Object.keys(compteurs)
    .map((nom) => ({
      nom,
      nombre: compteurs[nom],
      niveau: compteurs[nom] >= 3 ? 'critique' : 'a surveiller',
    }))
    .sort((a, b) => b.nombre - a.nombre);
}

function categoriserIncident(incident) {
  const texte = [
    incident && incident.type_projection,
    incident && incident.evenement_type,
    incident && incident.categorie_operationnelle,
    incident && incident.contenu_brut,
  ].filter(Boolean).join(' ').toLowerCase();

  if (texte.includes('upload') || texte.includes('publication')) return 'upload';
  if (texte.includes('202')) return 'etat 202';
  if (texte.includes('finalize')) return 'finalize';
  if (texte.includes('raw')) return 'preuve RAW';
  if (texte.includes('memoire') || texte.includes('memory')) return 'memoire';
  if (texte.includes('runtime') || texte.includes('exception') || texte.includes('error')) return 'runtime';
  if (texte.includes('camera')) return 'camera';
  return 'autre';
}

function scoreVersion(version) {
  return Number(version.nb_incidents || 0) * 3
    + Number(version.nb_202_actifs || 0) * 2
    + Number(version.nb_uploads_bloques || 0) * 3
    + Number(version.nb_finalize_suspects || 0) * 5
    + Number(version.nb_raw_absentes || 0) * 5;
}

function somme(items, champ) {
  return (items || []).reduce((total, item) => total + Number(item[champ] || 0), 0);
}

function nombre(valeur) {
  if (valeur === null || valeur === undefined || valeur === '') return '0';
  return String(valeur);
}

export default CockpitAmeliorationSysteme;



// WEBPACK FOOTER //
// src/components/cockpit/CockpitAmeliorationSysteme.js